import React from 'react'
import styled from '@emotion/styled'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Button, Modal, Space } from 'antd'
import {
  PortalArticleTopicBlock,
  PortalArticleTopicItem,
  PortalEditorSectionTarget,
} from '../../../../models/PortalArtigoModel'
import {
  createDefaultTopicImageItem,
  createDefaultTopicQuickSummaryItem,
  createDefaultTopicSectionItem,
  createPortalEditorBlockId,
  cloneSectionContentWithFreshTableIds,
} from '../../helpers/block-defaults'
import { getTopicLabel, moveTopicItem, topicHasQuickSummary } from '../../helpers/topic-utils'
import { PortalTopicItemEditor } from './PortalTopicItemEditor'
import { PortalSectionSaveControlProps } from '../../helpers/editor-section-save'

interface Props {
  topic: PortalArticleTopicBlock
  topicNumber: number
  topics: PortalArticleTopicBlock[]
  expanded: boolean
  canMoveUp: boolean
  canMoveDown: boolean
  onExpandedChange: (expanded: boolean) => void
  onChange: (topic: PortalArticleTopicBlock) => void
  onMove: (direction: -1 | 1) => void
  onDuplicate: () => void
  onRemove: () => void
  onMoveSubtopic: (itemId: string | undefined, targetTopicId: string) => void
  getSectionSaveControlProps: (target: PortalEditorSectionTarget) => PortalSectionSaveControlProps
}

export function PortalTopicEditor({
  topic,
  topicNumber,
  topics,
  expanded,
  canMoveUp,
  canMoveDown,
  onExpandedChange,
  onChange,
  onMove,
  onDuplicate,
  onRemove,
  onMoveSubtopic,
  getSectionSaveControlProps,
}: Props) {
  const mainIndex = topic.items.findIndex((item) => item.type === 'section' && item.role === 'main')
  const updateItem = (index: number, item: PortalArticleTopicItem) =>
    onChange({
      ...topic,
      items: topic.items.map((current, currentIndex) => (currentIndex === index ? item : current)),
    })

  const duplicateItem = (index: number) => {
    const source = topic.items[index]
    if (source.type === 'quickSummary') return
    const cloned = JSON.parse(JSON.stringify(source)) as PortalArticleTopicItem
    cloned.id = createPortalEditorBlockId(source.type)
    if (cloned.type === 'section') {
      cloned.section = {
        ...cloned.section,
        id: createPortalEditorBlockId('section'),
        anchorId: undefined,
        content: cloneSectionContentWithFreshTableIds(cloned.section.content),
      }
    }
    onChange({
      ...topic,
      items: [...topic.items.slice(0, index + 1), cloned, ...topic.items.slice(index + 1)],
    })
  }

  const addItem = (item: PortalArticleTopicItem) =>
    onChange({ ...topic, items: [...topic.items, item] })

  return (
    <TopicContainer id={`portal-editor-topic-${topic.id}`}>
      <TopicHeader>
        <TopicTitle>
          Tópico {topicNumber}: <span>{getTopicLabel(topic)}</span>
        </TopicTitle>
        <Space wrap>
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={!canMoveUp}
            onClick={() => onMove(-1)}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={!canMoveDown}
            onClick={() => onMove(1)}
          />
          <Button size="small" icon={<CopyOutlined />} onClick={onDuplicate}>
            Duplicar
          </Button>
          <Button size="small" onClick={() => onExpandedChange(!expanded)}>
            {expanded ? 'Recolher' : 'Expandir'}
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              Modal.confirm({
                title: 'Remover tópico',
                content:
                  'Todo o H2, subtópicos, imagens e resumo rápido deste tópico serão removidos.',
                okText: 'Remover',
                cancelText: 'Cancelar',
                okType: 'danger',
                onOk: onRemove,
              })
            }
          />
        </Space>
      </TopicHeader>

      {expanded && (
        <TopicBody>
          {topic.items.map((item, index) => {
            const crossingMainUp =
              (item.type === 'section' && item.role === 'subtopic') || item.type === 'quickSummary'
            const canMoveUp = index > 0 && !(crossingMainUp && index - 1 <= mainIndex)
            const mainMovingDownIntoStructuredItem =
              item.type === 'section' &&
              item.role === 'main' &&
              topic.items[index + 1] &&
              topic.items[index + 1].type !== 'image'
            return (
              <PortalTopicItemEditor
                key={item.id || `${item.type}-${index}`}
                item={item}
                itemIndex={index}
                itemsLength={topic.items.length}
                topic={topic}
                topics={topics}
                canMoveUp={canMoveUp}
                canMoveDown={index < topic.items.length - 1 && !mainMovingDownIntoStructuredItem}
                onChange={(nextItem) => updateItem(index, nextItem)}
                onMove={(direction) => onChange(moveTopicItem(topic, index, index + direction))}
                onDuplicate={
                  item.type === 'quickSummary' || (item.type === 'section' && item.role === 'main')
                    ? undefined
                    : () => duplicateItem(index)
                }
                onRemove={
                  item.type === 'section' && item.role === 'main'
                    ? undefined
                    : () =>
                        onChange({
                          ...topic,
                          items: topic.items.filter((_, current) => current !== index),
                        })
                }
                onMoveToTopic={
                  item.type === 'section' && item.role === 'subtopic'
                    ? (targetId) => onMoveSubtopic(item.id, targetId)
                    : undefined
                }
                saveControl={getSectionSaveControlProps({
                  scope: 'topicItem',
                  topicId: topic.id,
                  itemId: item.id || '',
                })}
              />
            )
          })}

          <AddActions>
            <Button
              icon={<PlusOutlined />}
              onClick={() => addItem(createDefaultTopicSectionItem('subtopic'))}
            >
              Subtópico (H3)
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => addItem(createDefaultTopicImageItem())}>
              Imagem
            </Button>
            <Button
              icon={<PlusOutlined />}
              disabled={topicHasQuickSummary(topic)}
              onClick={() => addItem(createDefaultTopicQuickSummaryItem())}
            >
              Resumo rápido
            </Button>
          </AddActions>
        </TopicBody>
      )}
    </TopicContainer>
  )
}

const TopicContainer = styled.section`
  padding: 32px;
  border-radius: 20px;
  background: #f4f4f4;
  color: #122a41;
`
const TopicHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`
const TopicTitle = styled.h3`
  margin: 0;
  color: #122a41;
  font-size: 18px;
  font-weight: 600;

  span {
    font-weight: 400;
  }
`
const TopicBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 24px;

  > .ant-card {
    border-color: #e1e4ed;
    border-radius: 8px;
  }
`
const AddActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 4px;
`
